import React, { useContext, useEffect, useState } from "react";
import { Dropdown, Icon, Message } from "semantic-ui-react";
import Button from "@material-ui/core/Button";
import CallIcon from "@material-ui/icons/Call";
import Alert from "@material-ui/lab/Alert";
import { InputAdornment, makeStyles, TextField, Theme } from "@material-ui/core";
import { getLogger } from "../services/LoggingService";
import { CallContext } from "../contexts/CallContext";
import { ConnectDataContext } from "../contexts/DataContext";
import { placeOutboundCall } from "../connectExtensions";

const logger = getLogger("OutboundCalls");

interface IState {
    availableQueues: any;
    outboundQueue: any;
    outboundNumber: string;
    error: any;
    initializingCall: boolean;
    buttonState: {
        text: string;
    };
}
const useStyles = makeStyles((theme: Theme) => ({
    mt10: {
        marginTop: "10px",
    },
}));

export function OutboundCalls() {
    const classes = useStyles();

    const [state, setState] = useState<IState>({
        availableQueues: [],
        outboundQueue: null,
        outboundNumber: "",
        error: null,
        initializingCall: false,
        buttonState: {
            text: "Call",
        },
    });

    const { agent, contact } = useContext(CallContext);
    const { setSelectedQueueName } = useContext(ConnectDataContext);

    useEffect(() => {
        if (!agent) return;

        getAvailableQueues(agent);
        setCountryCodes(agent);
    }, [agent]);

    useEffect(() => {
        if (!contact) return;

        contact.onConnecting(() => handleConnecting());
        contact.onConnected(() => handleConnected());
        contact.onEnded(() => handleEnded());
        contact.onError(() => handleOnError());
    }, [contact]);

    const handleConnecting = (): void => {
        setState({
            ...state,
            buttonState: {
                ...state.buttonState,
                text: "Connecting call ...",
            },
        });
    };
    const handleConnected = (): void => {
        setState({
            ...state,
            buttonState: {
                ...state.buttonState,
                text: "Connected",
            },
        });
    };
    const handleEnded = (): void => {
        setState({
            ...state,
            buttonState: {
                ...state.buttonState,
                text: "Call",
            },
        });
        clearInputs();
    };
    const handleOnError = (): void => {
        setState({
            ...state,
            buttonState: {
                ...state.buttonState,
                text: "Error calling",
            },
            error: "Outbound call error",
        });
        setTimeout(() => {
            setState({
                ...state,
                buttonState: {
                    ...state.buttonState,
                    text: "Call",
                },
                error: "",
            });
        }, 5000);
    };

    const setCountryCodes = async (agent: connect.Agent): Promise<void> => {
        logger.debug("Getting available country codes ...");
        try {
            const countries = await agent.getDialableCountries();
            logger.debug("Countries ", countries);
        } catch (err) {
            logger.error("Error retrieving countries");
        }
    };
    const getAvailableQueues = async (agent: connect.Agent): Promise<void> => {
        logger.debug("Getting available queues ...");
        try {
            const { queues, name } = await agent.getRoutingProfile();
            logger.debug(
                "Queues",
                queues.map((q) => q.name),
                name
            );
            setState({
                ...state,
                availableQueues: normalizeQueuesForList(queues),
            });
        } catch (err) {
            logger.debug("No available queues");
        }
    };
    const normalizeQueuesForList = (queues: any): void => {
        return queues
            .filter((queue: any) => !!queue.name)
            .map((queue: any, i: number) => ({
                key: `queue-${i + 1}`,
                text: queue.name,
                value: queue.queueARN,
            }));
    };
    const handleQueueSelectionChange = (event: any, data: any): void => {
        const queue = state.availableQueues.find((q: any) => q.value === data.value);
        if (queue) {
            setSelectedQueueName(queue.text);
        } else {
            setSelectedQueueName("");
        }

        setState({ ...state, outboundQueue: data.value });
    };

    const onCallSuccess = (): void => {
        logger.debug("Call was successfull");
        setState({
            ...state,
            initializingCall: false,
        });
    };
    const clearInputs = (): void => {
        setState({
            ...state,
            outboundQueue: null,
            outboundNumber: "",
            initializingCall: false,
        });
    };
    const onCallFailure = (err: any): void => {
        logger.error("onCallFailure error:", err);
        err = JSON.parse(err);
        setState({
            ...state,
            error: err.message ? err.message : "Outbound call error.  Please try again",
            initializingCall: false,
        });
        //props.context.setAlert("Call Failed", "error");
        setTimeout(() => {
            setState({
                ...state,
                error: "",
            });
        }, 3500);
    };
    const handleClick = (): void => {
        if (!agent) return;

        const agentContacts = agent.getContacts(connect.ContactType.VOICE);
        if (agentContacts.length === 0) {
            logger.debug("no contacts");

            const parts = `+${state.outboundNumber}`.split("x");
            const number = parts[0];
            const extension = parts.length === 2 ? parts[1] : "";

            placeOutboundCall(agent, number, state.outboundQueue, extension).then(onCallSuccess).catch(onCallFailure);
        } else if (agentContacts.length === 1) {
            logger.debug("Already have one contact");
        } else {
            throw new Error("Already have 2 active voice contacts");
        }
        setState({
            ...state,
            initializingCall: true,
        });
    };

    const paramsValid = (): boolean => {
        return !!state.outboundNumber.trim() && phoneValid(state.outboundNumber) && !!state.outboundQueue;
    };

    const phoneValid = (ani: string): boolean => {
        try {
            const phoneRegex = RegExp(/^\d{11}$/g);
            return phoneRegex.test(ani);
        } catch (err) {
            logger.debug("Number check failed returning false");
            return false;
        }
    };
    const agentOnCall = (): boolean => {
        return state.buttonState.text === "Connected" || state.buttonState.text === "Connecting call ...";
    };
    const handleInputChange = (prop: string) => (event: any) => {
        const newVal = event.target.value;
        if (prop === "outboundNumber" && newVal.length > 11) return;
        setState({ ...state, [prop]: newVal });
    };

    const callError = state.error ? <Alert severity={"error"}>{state.error}</Alert> : null;
    if (state.availableQueues.length === 0) {
        return <Message warning content="No available queues." />;
    }
    return (
        <div id="outbound-call-options">
            <TextField
                label="Outgoing Number"
                id="outlined-margin-dense"
                className="phoneInputRow"
                onChange={handleInputChange("outboundNumber")}
                InputProps={{
                    startAdornment: <InputAdornment position="start">+</InputAdornment>,
                }}
                margin="dense"
                variant="outlined"
                value={state.outboundNumber}
            />
            <Dropdown
                placeholder="Select Outgoing Queue"
                fluid
                search
                selection
                clearable
                value={state.outboundQueue}
                options={state.availableQueues}
                onChange={handleQueueSelectionChange}
            />
            {/* <Button
                loading={state.initializingCall}
                disabled={!paramsValid() || agentOnCall()}
                className="action-button"
                onClick={handleClick}
            >
                <Icon size="small" name="phone" />
                {state.buttonState.text}
            </Button> */}
            <Button
                disabled={!paramsValid() || agentOnCall()}
                variant="contained"
                color="primary"
                size="small"
                fullWidth
                className={classes.mt10}
                endIcon={<CallIcon />}
                onClick={handleClick}
            >
                {state.buttonState.text}
            </Button>
            {callError}
        </div>
    );
}
